
import { PARegistry } from "../singletons/pa-registry";
import type { UserService } from "src/app/_services"
import type { PlanningAssistantService } from "src/app/_services/planning-assistant.service"
import type { PACoordinates } from "./coordinates"
import { PAProgress } from "./progress"
import type { PAProject } from "./project"
import { PAUtil } from "./util"
import { PATechnicianDate } from "./technician-date"
import type { CalendarWeek, CalendarWeekData, MapRoute } from "src/app/_models/planning-assistant.interface"
import type { CalendarComponent } from "../calendar/calendar.component"
import { PALocation } from "./location"
import type { PATicket } from "./ticket"
import type { Priority } from "src/app/_models/priority.interface"
import type { PAOperation } from "./operation"
import type { MaterialContainer } from "./material-container/material-container.interface"
import type { MarkerInstance } from "./mapbox-marker"
import { FireUpdateOnChange } from "./util-classes/fire-update-on-change";
import type { PADataControl } from "../singletons/pa-data-control";
import type { WarehouseWithProjectBins } from "../../../_models/warehouse.interface";
import type { StorageBinWithProjectBins } from "../../../_models/storage-bin.interface";
import type { ArticleTemplateHash } from "../../../_models/article.interface";
import type { AbsenceUser } from "../../../_models/absence.model";
import type { Absence } from "../../../_models/absence.interface";
import type { HolidaysTypes } from "date-holidays";

export class PATechnician extends FireUpdateOnChange<void> implements MarkerInstance {

  static pseudoTechnicianNames: string[] = ['offene Planung', 'Bentomax Bereitschaft', 'nicht vergeben', 'Kein Einsatz notwendig', 'Materialerfassung Kiel']

  // Lebt hier statt in PAFilter, weil die Pseudo-Namen Technician-Domäne sind —
  // und PAFilter sonst einen Wert-Import-Zyklus mit PATechnician bildet
  static filterOutPseudoTechnicians(technicians: PATechnician[]): PATechnician[] {
    return technicians.filter(technician => PATechnician.pseudoTechnicianNames.indexOf(technician.firstname + ' ' + technician.lastname) < 0)
  }
  static planningAssistantService: PlanningAssistantService
  static userService: UserService
  static calendarComponent: CalendarComponent

  operationCounts?: {
    project: Map<number, number>,
    priority: Map<number, number>
  }
  technicianDates = new Map<number, PATechnicianDate>()
  public loadedCalendarWeekDataUpdates: { cwd: CalendarWeekData, last_update: number }[] = []
  public location: PALocation
  public warehouses: WarehouseWithProjectBins[]
  private loadingWarehouses = false

  private _color?: { r: number, g: number, b: number, a: number }

  public priorityBasedOperationTimes = new Map<number, { operation_id: number, duration: number, date: string }[]>()
  private loadingPriorityIds = []
  public currentTourPlannings: TechnicianTourPlanning

  public placeholderTechnicianIds: number[] = []

  public calendarWeekWorkloadMap = new Map<string, number>()

  constructor(
    public id: number,
    public locationID: number,
    public firstname: string,
    public lastname: string,
    public state: string,
    public country: string,
    public coordinates: PACoordinates,
    public company: string,
    public zipCode: string,
    public companyAddressCompany: string,
    public telephones: string[],
    public roles: string[],
    private driving_time_factor: number,
    readonly absenceUserId?: string,
  ) {
    super()
    if (PARegistry.dataControl.locationMap.has(locationID)) {
      this.location = PARegistry.dataControl.locationMap.get(locationID)
    } else {
      this.location = new PALocation(coordinates, PARegistry.dataControl.distanceCalculator, locationID, zipCode, '', '')
    }
    PARegistry.dataControl.technicianMap.set(id, this)
    PARegistry.dataControl.allTechnicians = PARegistry.dataControl.allTechnicians.concat(PATechnician.filterOutPseudoTechnicians([this]))
    this.operationCounts = {
      priority: new Map<number, number>(),
      project: new Map<number, number>()
    }
    this.currentTourPlannings = new TechnicianTourPlanning(this)

    this.fireUpdate()
  }

  get color(): { r: number, g: number, b: number, a: number } {
    return this._color
  }

  get drivingTimeFactor(): number {
    return PARegistry.settingsControl.selectedSettingConfig.data.tour_planning.use_driving_time_factor ? this.driving_time_factor : 1
  }

  set color(color: { r: number, g: number, b: number, a: number }) {
    this._color = color
    this.fireUpdateManually()
  }

  getCoordinates(): PACoordinates {
    return this.location.coordinates
  }

  executeBeforeChange(): void {
    return
  }

  getCurrentCalendarWeekTechnicianDates(): PATechnicianDate[] {
    return PARegistry.timeControl.getCurrentCalendarWeekDayFilteredTimestamps().map(ts => this.getTechnicianDate(ts, true, true))
  }

  public getColorString(change_alpha?: number): string {
    const preferred_technicians = PARegistry.filterControl.preferredCalendarTechnicians

    const rgba = this.color || (preferred_technicians.includes(this) ? {
      r: 0,
      g: 127,
      b: 211,
      a: 1
    } : PAUtil.hexToRgba('#ffffffaa'))

    return change_alpha ? PAUtil.rgbaToString({
      r: rgba.r,
      g: rgba.g,
      b: rgba.b,
      a: change_alpha
    }) : PAUtil.rgbaToString(rgba)
  }

  getDistanceToCoordinatesAsTheCrowFlies(lat: number, lon: number): number {
    return this.location.getDistanceToCoordinatesAsTheCrowFlies(lat, lon)
  }

  getDistanceToLocationAsTheCrowFlies(location: PALocation): number {
    return this.location.getDistanceToCoordinatesAsTheCrowFlies(location.coordinates.latitude, location.coordinates.longitude)
  }

  public getLoadedActiveProjects(apply_global_filter?: boolean): PAProject[] {
    const projects = PARegistry.dataControl.loadedProjects.filter(project => project.active_technician_ids.length == 0 || project.active_technician_ids.indexOf(this.id) >= 0)
    return apply_global_filter ? projects.filter(p => PARegistry.filterControl.selectedProjects.includes(p)) : projects
  }

  async loadWarehousesWithArticles(): Promise<void> {
    if (!this.warehouses && !this.loadingWarehouses) {
      const progress = new PAProgress(`Lade Artikel von ${this.getFullName()}`, 1)
      this.loadingWarehouses = true
      this.warehouses = []
      return new Promise<void>(resolve => {
        progress.addSubProgress(PATechnician.userService.getWarehousesWithProjectBins(this.id), this.firstname + ' ' + this.lastname).subscribe(
          data => {
            this.warehouses = data
            this.loadingWarehouses = false
            resolve()
          },
          err => {
            console.log(err)
            this.loadingWarehouses = false
            resolve()
          }
        )
      })
    } else if (this.loadingWarehouses) {
      await PAUtil.sleepUntilConditionBreaks(
        () => {
          return this.loadingWarehouses
        }
      )
    }
  }

  async loadDistancesToCoordinates(lat: number, long: number): Promise<void> {
    return new Promise((resolve) => {
      PATechnician.userService.distancesToCoordinates([this.id], lat, long).subscribe(
        async (data) => {
          for (const dist_obj of data) {
            const technician = PARegistry.dataControl.getTechnician(dist_obj.id)
            const distance_calculator = PARegistry.dataControl.distanceCalculator
            const operation_to_coordinates_key = distance_calculator.coordinatesToDistanceKey(lat, long, technician.coordinates.latitude, technician.coordinates.longitude)
            distance_calculator.realPositionDistances.set(operation_to_coordinates_key, {
              distance: Math.round(dist_obj.distance),
              duration: Math.round(dist_obj.duration)
            })
          }
          resolve()
        },
        (err) => {
          console.error(err)
          resolve()
        }
      )
    })
  }

  public getTechnicianDate(timestamp: number, init_if_not_existent?: boolean, load_data?: boolean): PATechnicianDate {
    if (!this.technicianDates.has(timestamp) && init_if_not_existent) {
      this.initTechnicianDate(timestamp, load_data)
    }
    return this.technicianDates.get(timestamp)
  }

  public isBentomaxTechnician(): boolean {
    let address_company_matches = false
    if (this.company) {
      address_company_matches = this.company.toLowerCase().includes('bentomax')
    }
    let company_address_company_matches = false
    if (this.companyAddressCompany) {
      company_address_company_matches = this.companyAddressCompany.toLowerCase().includes('bentomax')
    }
    return (address_company_matches || company_address_company_matches) && this.roles.indexOf('SUBCONTRACTOR') < 0
  }

  public isProjectTechnician(project_id: number): boolean {
    const project_technician_ids = PARegistry.dataControl.getProject(project_id).active_technician_ids
    return project_technician_ids.indexOf(this.id) >= 0 || !project_technician_ids.length
  }

  public async loadPriorityBasedOperationTimes(priority_id: number): Promise<boolean> {
    if (!this.operationTimeEntryForPriorityWasLoaded(priority_id)) {
      if (!this.loadingPriorityIds.includes(priority_id)) {
        this.loadingPriorityIds.push(priority_id)
        return new Promise((resolve) => {
            PATechnician.userService.priorityBasedOperationTimes(this.id, priority_id).subscribe(
              (data) => {
                this.priorityBasedOperationTimes.set(priority_id, data)
                PAUtil.removeElementFromList(this.loadingPriorityIds, priority_id)
                resolve(true)
              },
              (err) => {
                console.error(err)
                PAUtil.removeElementFromList(this.loadingPriorityIds, priority_id)
                resolve(false)
              }
            )
          }
        )
      } else {
        await PAUtil.sleepUntilConditionBreaks(
          () => {
            return this.loadingPriorityIds.includes(priority_id)
          }
        )
        return
      }
    }
  }

  public async loadTechnicianProjectOperationCounts(project_id: number): Promise<void> {
    this.operationCounts.project.set(project_id, 0)
    return new Promise((resolve) => {
      PATechnician.userService.project_operation_count(this.id, project_id).subscribe(
        async (data) => {
          this.operationCounts.project.set(project_id, data.done_project_operations_count)
          resolve()
        },
        (err) => {
          console.error(err)
          resolve()
        },
      )
    })
  }

  public async loadTechnicianPriorityOperationCounts(priority_id: number): Promise<void> {
    this.operationCounts.priority.set(priority_id, 0)
    return new Promise((resolve) => {
      PATechnician.userService.priority_operation_count(this.id, priority_id).subscribe(
        async (data) => {
          this.operationCounts.priority.set(priority_id, data.done_priority_operations_count)
          resolve()
        },
        (err) => {
          console.error(err)
          resolve()
        },
      )
    })
  }

  public async updateTechnicianExperience(project_id: number, priority_id: number) {
    if (!this.operationCounts.priority.has(priority_id)) {
      await this.loadTechnicianPriorityOperationCounts(priority_id)
    }
    if (!this.operationCounts.project.has(project_id)) {
      await this.loadTechnicianProjectOperationCounts(project_id)
    }
  }

  public getPriorityBasedAverageOperationTime(priority_id: number): number {
    const operation_times = this.getOperationTimes(priority_id)
    if (operation_times) {
      return PAUtil.averageTime(operation_times.map(operation_time => operation_time.duration))
    } else {
      return -1
    }
  }

  public getAverageOperationTime(operation: PAOperation): number {
    const average_time = this.getPriorityBasedAverageOperationTime(operation.ticket.priority.id)
    if (average_time > 0) {
      return average_time
    } else {
      return operation.ticket.time_estimation
    }
  }

  public getOperationTimes(priority_id: number): { operation_id: number, duration: number, date: string }[] {
    if (this.operationTimeEntryForPriorityWasLoaded(priority_id)) {
      return this.priorityBasedOperationTimes.get(priority_id)
    } else {
      return []
    }
  }

  public operationTimeEntryForPriorityWasLoaded(priority_id: number): boolean {
    return this.priorityBasedOperationTimes.has(priority_id)
  }

  public async getAbsencesForTimestamp(ts: number): Promise<Absence[]> {
    if (!this.absenceUserId) return []
    const date_string = PARegistry.timeControl.timestampToUTCDatestring(ts, true, '-')
    const absence_calendar = PARegistry.dataControl.absenceCalendar
    await absence_calendar.loadAbsencesBetweenDates(date_string)
    return absence_calendar.getLoadedUserAbsencesOnDate(this.absenceUserId, date_string)
  }

  public getAbsenceUser(): AbsenceUser {
    return this.absenceUserId ? PARegistry.dataControl.absenceCalendar.absenceUserMap.get(this.absenceUserId) : null
  }

  public hasShiftOnDay(day_ts: number): boolean {
    const weekday = PARegistry.timeControl.getTimeStampsWeekDay(day_ts)
    const absence_user = this.getAbsenceUser()
    const possible_shift = absence_user ? absence_user.hasShiftOnWeekdayWithFallback(weekday, day_ts) : PARegistry.timeControl.workDays.concat(['saturday']).includes(weekday)
    return !!possible_shift
  }

  public initTechnicianDate(utc_timestamp: number, load_data?: boolean) {
    const week_day = PARegistry.timeControl.getTimeStampsWeekDay(utc_timestamp)
    const holidays =  this.getLocalHolidays(utc_timestamp)
    const utc_date = PARegistry.timeControl.timestampToDatestring(utc_timestamp, false)
    const local_date = new Date(utc_timestamp)
    local_date.setHours(0)
    const date_in_hawk_link_format = utc_date.split('-').join('.')
    const technician_date = new PATechnicianDate(
      this,
      utc_date,
      {
        week_day: week_day,
        utc_timestamp: utc_timestamp,
        local_timestamp: local_date.getTime(),
        holidays: holidays,
        date_string: PARegistry.timeControl.weekDayToGermanName(week_day, true) + ' ' + PARegistry.timeControl.timestampToDatestring(utc_timestamp, false, '.')
      },
      '/a/ticket/suche?operation_date_from=' + date_in_hawk_link_format + '&operation_date_to=' + date_in_hawk_link_format + '&technician_ids=' + this.id,
      PARegistry.dataControl.generateNextOperatorDayId()
    )
    this.technicianDates.set(utc_timestamp, technician_date)

    if (load_data) {
      technician_date.setTravelTechnicianDate().then(
        _ => {
          technician_date.loadData()
        }
      )
    }
  }

  public getTotalTechnicianWorktimeOfCalendarWeekInHours(calendar_week: CalendarWeek, operation_time_filter: 'average' | 'planned'): number {
    const calendar_week_data = PARegistry.timeControl.getCalendarWeekData(calendar_week)
    if (calendar_week_data.calendar_week.year == calendar_week.year && calendar_week_data.calendar_week.number == calendar_week.number) {
      const worktime_in_minutes = PARegistry.timeControl.weekDays.reduce((sum, weekday) => sum + this.getTechnicianDate(calendar_week_data.weekdays[weekday].timestamp, true).tour.getTotalWorkTime(true, operation_time_filter), 0)
      return Math.round(worktime_in_minutes / 60)
    }
    return 0
  }

  public getFullName(cut_at?: number): string {
    const full_name = this.firstname + ' ' + this.lastname
    return ((cut_at && full_name.length > cut_at) ? full_name.slice(0, cut_at) + '...' : full_name)
  }

  public getNameAbbreviation(): string {
    return `${this.firstname[0]}. ${this.lastname[0]}. `
  }

  public getAirDistanceRouteToLocation(location: PALocation, color: string): MapRoute {
    return this.location.getAirDistanceRouteToLocation(location, color)
  }

  public hasTicketMaterial(ticket: PATicket): boolean {
    const container_with_material = [ticket, ticket.afterStorageTaskMaterialContainer].filter(container => container?.materials?.length)
    return container_with_material.length ? this.hasContainerMaterial(container_with_material) : true
  }

  public hasContainerMaterial(all_container: MaterialContainer[]): boolean {
    if (!this.warehouses?.length) {
      return !all_container.find(c => c?.materials?.length)
    }

    const container_without_project = all_container.filter(container => !container.project)
    const container_with_project = all_container.filter(container => container.project)

    const all_bins = this.warehouses.reduce((sum: StorageBinWithProjectBins[], warehouse) => sum.concat(warehouse.bins), [])
    const all_bins_copy = JSON.parse(JSON.stringify(all_bins)) as StorageBinWithProjectBins[]
    const bins_without_project = all_bins_copy.filter(bin => !bin.project_bins.find(pb => pb.project_id))
    const bins_with_project = all_bins_copy.filter(bin => bin.project_bins.find(pb => pb.project_id))
    const project_grouped_bins = PAUtil.groupBy(bins_with_project, bin => bin.project_bins.filter(pb => pb.project_id)[0].project_id)

    let has_enough_material = true

    function emptyContainerMaterialFromBins(container: MaterialContainer, bins: StorageBinWithProjectBins[]) {
      for (const material of container.materials) {
        const material_amount = material.amount
        const material_template_id = material.template_id
        let material_amount_found = 0

        for (const bin of bins) {
          const material_articles = bin.articles.filter(article => article.template_id == material_template_id)

          while (material_articles.length && material_amount_found < material_amount) {
            const next_article = material_articles.shift()
            PAUtil.removeElementFromList<ArticleTemplateHash>(bin.articles, next_article)
            material_amount_found += 1
          }
        }

        if (material_amount_found < material_amount) {
          has_enough_material = false
          break
        }
      }
    }

    for (const container of container_without_project) {
      emptyContainerMaterialFromBins(container, bins_without_project);
      if (!has_enough_material) return false
    }

    for (const container of container_with_project) {
      const use_bins = project_grouped_bins[container.project.id] || []
      emptyContainerMaterialFromBins(container, use_bins.concat(bins_without_project));
      if (!has_enough_material) return false
    }

    return true
  }

  public hasOperationMaterial(operation: PAOperation): boolean {
    if (operation.ticket) {
      return this.hasTicketMaterial(operation.ticket)
    } else {
      return true
    }
  }

  public getWeekWorktimeForCalendarWeekData(cwd: CalendarWeekData): number {
    let total_work_minutes = 0
    for (const weekday of PARegistry.timeControl.weekDays) {
      const day_timestamp: number = cwd.weekdays[weekday].timestamp
      total_work_minutes += this.getPlannedWorkMinutesOnDay(day_timestamp)
    }
    return Math.round(total_work_minutes / 60)
  }

  public getCalendarWeekDataUpdate(cw: CalendarWeek): { cwd: CalendarWeekData, last_update: number } {
    return this.loadedCalendarWeekDataUpdates.filter(cwdu => cwdu.cwd.calendar_week.number == cw.number && cwdu.cwd.calendar_week.year == cw.year)[0]
  }

  public getLastCalendarWeekDataUpdateTimestamp(cw: CalendarWeek): number {
    const cwdu = this.getCalendarWeekDataUpdate(cw)
    if (cwdu) {
      return cwdu.last_update
    } else {
      return 0
    }
  }

  updateCalendarWeekWorkload(cw_keys: string): void {
    const [number, year] = cw_keys.split('-').map(digit => Number.parseInt(digit))
    this.calendarWeekWorkloadMap.set(`${number}-${year}`, this.calendarWeekWorkloadInPercent({year, number}))
  }

  public calendarWeekWorkloadInPercent(cw: CalendarWeek): number {
    const cwd = PARegistry.timeControl.getCalendarWeekData(cw)
    let target_work_minutes = 0
    let total_work_minutes = 0
    for (const weekday of PARegistry.timeControl.weekDays) {
      const day_timestamp: number = cwd.weekdays[weekday].timestamp
      target_work_minutes += PARegistry.timeControl.workDays.includes(weekday) ? this.getTechnicianDate(day_timestamp, true).getTargetWorkMinutes() : 0
      total_work_minutes += this.getPlannedWorkMinutesOnDay(day_timestamp)
    }
    return target_work_minutes ? Math.round((total_work_minutes / target_work_minutes) * 100) : 100
  }

  getPlannedWorkMinutesOnDay(days_ts: number): number {
    return this.getTechnicianDate(days_ts, true).tour.route.time_specific_data[PARegistry.filterControl.selectedOperationTimeFilter].total_time
  }

  getLocalPublicHolidays(utc_timestamp: number): HolidaysTypes.Holiday[] {
    return this.getLocalHolidays(utc_timestamp).filter(hd => hd.type == 'public')
  }

  getLocalRegionalHolidays(utc_timestamp: number): HolidaysTypes.Holiday[] {
    return this.getLocalHolidays(utc_timestamp).filter(hd => hd.type == 'observance')
  }

  getLocalHolidays(utc_timestamp: number): HolidaysTypes.Holiday[] {
    const holiday_state = PARegistry.timeControl.germanStatesTranslations[this.state]
    return PARegistry.timeControl.getTimestampsHolidays(utc_timestamp, holiday_state)
  }

  public async getPriorityOperationCountUntilTimestamp(priority: Priority, timestamp: number): Promise<number> {
    return this.currentTourPlannings.getPriorityOperationCountBeforeTimestamp(priority, timestamp)
  }

  static setPlanningAssistantService(service: PlanningAssistantService): void {
    this.planningAssistantService = service
  }

  static setUserService(service: UserService): void {
    this.userService = service
  }

  static setCalendarComponent(component: CalendarComponent): void {
    this.calendarComponent = component
  }

  static initTechnicianDatesForCalendarWeeksData(technicians: PATechnician[], calendar_weeks_data: CalendarWeekData[], data_service: PADataControl) {
    for (const calendar_week_data of calendar_weeks_data) {
      this.initTechnicianDatesForCalendarWeek(technicians, calendar_week_data, data_service)
    }
  }

  static async initTechnicianDatesForCalendarWeek(technicians: PATechnician[], calendar_week_data: CalendarWeekData, data_service: PADataControl): Promise<void> {
    for (const technician of technicians) {
      const filtered_loaded_cwds = technician.loadedCalendarWeekDataUpdates.filter(cwdu => cwdu.cwd.calendar_week.year == calendar_week_data.calendar_week.year && cwdu.cwd.calendar_week.number == calendar_week_data.calendar_week.number)
      if (!filtered_loaded_cwds.length) {
        technician.loadedCalendarWeekDataUpdates.push({cwd: calendar_week_data, last_update: 0})
      }
    }
    await data_service.loadTechnicianDatesDataForCalendarWeekData(technicians, calendar_week_data)
  }

  static async initTechnicianDateForCalendarDay(technicians: PATechnician[], timestamp: number) {
    for (const technician of technicians) {
      if (!technician.technicianDates.has(timestamp)) {
        technician.initTechnicianDate(timestamp, true)
      } else {
        const technician_date = technician.technicianDates.get(timestamp)
        if (technician_date.loadingStatus == 'init') {
          technician_date.loadData().then(_ => technician_date.tour.updateRoute())
        }
      }
    }
  }

}


export class TechnicianTourPlanning extends FireUpdateOnChange<{ priority: Priority, start_ts: number }[]>{

  executeBeforeChange(): void {
  }

  private _technicianDatesInPlanning: PATechnicianDate[] = []

  constructor(
    private _technician: PATechnician
  ) {
    super()
  }

  get technicianDatesInPlanning() {
    return this._technicianDatesInPlanning
  }

  set technicianDatesInPlanning(tours: PATechnicianDate[]) {
    const tours_before = this._technicianDatesInPlanning
    this._technicianDatesInPlanning = tours

    Promise.all(tours.map(async td => await td.tour.updateRoute())).then(_ => this.firePriorityCountChangeBetweenTechnicianDates(tours_before, tours))
  }

  private firePriorityCountChangeBetweenTechnicianDates(tours_before: PATechnicianDate[], tours_after: PATechnicianDate[]): void {
    const all_tour_timestamps = tours_before.concat(tours_after).map(tour => tour.day.utc_timestamp)
    const min_day_ts = Math.min(...all_tour_timestamps)
    const max_day_ts = Math.max(...all_tour_timestamps)

    const priority_change_starts: { priority: Priority, start_ts: number }[] = []
    for (let ts = min_day_ts; ts <= max_day_ts; ts += 24 * 60 * 60 * 1000) {
      const tour_before = tours_before.find(tour => tour.day.utc_timestamp == ts) || this._technician.getTechnicianDate(ts)
      const tour_after = tours_after.find(tour => tour.day.utc_timestamp == ts) || this._technician.getTechnicianDate(ts)

      if (tour_before != tour_after) {
        const priorities = [...new Set(tour_before.tour.operations.concat(tour_after.tour.operations).map(op => op.ticket.priority))]
        for (const priority of priorities) {
          if (!priority_change_starts.find(change_start => change_start.priority == priority)) {
            const priority_count_before = tour_before.tour.operations.filter(op => op.ticket.priority.id == priority.id).length
            const priority_count_after = tour_after.tour.operations.filter(op => op.ticket.priority.id == priority.id).length
            if (priority_count_before != priority_count_after) {
              priority_change_starts.push({priority: priority, start_ts: ts})
            }
          }
        }
      }
    }
    this.fireUpdate(priority_change_starts)
  }

  async getPriorityOperationCountBeforeTimestamp(priority: Priority, timestamp: number): Promise<number> {
    await this._technician.loadPriorityBasedOperationTimes(priority.id)
    const done_priority_operation_count = this._technician.getOperationTimes(priority.id).length

    let current_day_timestamp = PARegistry.timeControl.dateToTimestamp(new Date(Date.now()), true, true)
    const day_timestamps_until_timestamp: number[] = []
    while (current_day_timestamp < timestamp) {
      day_timestamps_until_timestamp.push(current_day_timestamp)
      current_day_timestamp += 24 * 60 * 60 * 1000
    }

    const loaded_tours_until_timestamp_promises = day_timestamps_until_timestamp.map(
      async ts => {
        const tour = this.technicianDatesInPlanning.find(td => td.day.utc_timestamp == ts) || this._technician.getTechnicianDate(ts, true, true)
        await tour.waitUntilDataWasLoaded()
        return tour
      }
    )

    const loaded_tours_until_timestamp = await Promise.all(loaded_tours_until_timestamp_promises)
    const planned_priority_operations_until_timestamp = loaded_tours_until_timestamp.reduce(
      (operations: PAOperation[], tour: PATechnicianDate) => {
        return operations.concat(tour.tour.operations.filter(op => op.ticket.priority.id == priority.id && !op.hasStarted() && op.getPlannedTimestamp() < timestamp))
      }, []
    )

    return done_priority_operation_count + planned_priority_operations_until_timestamp.length
  }

  async getRoutesPriorityOperationCountsOnDay(route: PAOperation[], day_timestamp: number): Promise<{
    operation: PAOperation,
    count: number
  }[]> {
    const route_priorities = [...new Set(route.map(op => op.ticket.priority))]
    const priority_counts_until_day = await Promise.all(route_priorities.map(async prio => {
      return await this.getPriorityOperationCountBeforeTimestamp(prio, day_timestamp)
    }))

    const res: { operation: PAOperation, count: number }[] = []
    const route_priority_counts = new Map<number, number>()
    for (const op of route) {
      const prio_id = op.ticket.priority.id
      if (!route_priority_counts.has(prio_id)) {
        route_priority_counts.set(prio_id, 0)
      }
      const route_priority_count = route_priority_counts.get(prio_id) + 1
      route_priority_counts.set(prio_id, route_priority_count)
      const prio_idx = route_priorities.findIndex(prio => prio_id == prio.id)

      res.push({operation: op, count: priority_counts_until_day[prio_idx] + route_priority_count})

    }

    return res
  }
}
